import { useCallback, useEffect, useMemo, useState } from 'react'
import * as React from 'react'
import styled from 'styled-components'
import { addMonths } from 'lib/datetime/dateUtils'
import BasicCalendar from './BasicCalendar'
import VerticalDatePickerHeader from './VerticalDatePickerHeader'
import VerticalSpacer from '../Spacing/VerticalSpacer'
import moment, { type Moment } from 'moment'
import SearchDatePickerDay from 'components/SearchV2/Components/Mobile/DateSearchFloatingPanel/SearchDatePickerDay'
import { useInView } from 'react-intersection-observer'

const INITIAL_MONTHS = 3
const LOAD_MORE_MONTHS = 2

// Scoped override: opacity ensures disabled days are visually dimmed regardless
// of child Typography color overrides in SearchDatePickerDay
const MonthsContainer = styled(VerticalSpacer)`
  button:disabled {
    opacity: 0.4;
  }
`

interface Props {
  startDate?: Moment
  endDate?: Moment | null
  min: Date
  max: Date
  onDatesChange?: (dates: { startDate: Moment; endDate?: Moment }) => void
  allowSameDayRange?: boolean
  className?: string
  contentRef?: React.RefObject<HTMLElement | null>
}

function VerticalDateRangePicker(props: Props) {
  const { startDate, endDate, min, max, onDatesChange, allowSameDayRange, className, contentRef } = props

  const onDayClick = useCallback((selectedDay: Date) => {
    const day = moment(selectedDay)
    const isSameStartDay = startDate && day.isSame(startDate, 'day')
    const shouldSelectEndDate = startDate && !endDate && (day.isAfter(startDate) || (allowSameDayRange && isSameStartDay))

    if (shouldSelectEndDate) {
      onDatesChange?.({ startDate, endDate: day })
    } else {
      onDatesChange?.({ startDate: day, endDate: undefined })
    }
  }, [allowSameDayRange, endDate, onDatesChange, startDate])

  const period = useMemo(() => {
    const monthsDiffs = moment(max).diff(min, 'months')
    const dates: Array<Date> = [min]
    for (let i = 0; i < monthsDiffs; i++) {
      dates.push(addMonths(dates[i], 1))
    }
    return dates
  }, [min, max])

  const [monthsToShow, setMonthsToShow] = useState(Math.min(INITIAL_MONTHS, period.length))

  const [sentinelRef, sentinelInView] = useInView({
    root: contentRef?.current,
    rootMargin: '0px 0px 100px 0px',
    skip: !contentRef?.current,
    initialInView: !contentRef?.current,
  })

  useEffect(() => {
    if (sentinelInView) {
      setMonthsToShow(prev => Math.min(prev + LOAD_MORE_MONTHS, period.length))
    }
  }, [sentinelInView, period.length])

  // Pre-computed timestamps for O(1) comparisons — avoids creating a moment()
  // object for every day on every re-render
  const startTs = startDate?.valueOf() ?? -1
  const endTs = endDate?.valueOf() ?? -1
  const minTs = min.getTime()
  const maxTs = max.getTime()

  const dayRender = useCallback((day: Date) => {
    const dayTime = day.getTime()
    const selected = dayTime === startTs || dayTime === endTs
    const disabled = dayTime < minTs || dayTime > maxTs
    const isInRange = !selected && startTs !== -1 && endTs !== -1
      ? dayTime > startTs && dayTime < endTs
      : false
    const isSingleDayRange = selected && startTs !== -1 && endTs !== -1 && startTs === endTs

    return (
      <SearchDatePickerDay
        key={dayTime}
        day={day}
        selected={selected}
        disabled={disabled}
        isInRange={isInRange}
        isSingleDayRange={isSingleDayRange}
        onClick={onDayClick}
      />
    )
  }, [startTs, endTs, minTs, maxTs, onDayClick])

  const visiblePeriod = period.slice(0, monthsToShow)

  return (
    <MonthsContainer gap={32} className={className}>
      {visiblePeriod.map(d => (
        <VerticalSpacer gap={16} key={d.toString()}>
          <VerticalDatePickerHeader currentDate={d} />
          <BasicCalendar
            month={d.getMonth()}
            year={d.getFullYear()}
            dayRender={dayRender}
            fixedHeight={false}
          />
        </VerticalSpacer>
      ))}
      {monthsToShow < period.length && <div ref={sentinelRef} />}
    </MonthsContainer>
  )
}

export default React.memo(VerticalDateRangePicker)
